import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiSdkService } from 'src/app/services/api-sdk.service';

interface ApiKeyItem {
  _id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  decryptedKey?: string;
}

/**
 * Placeholder rendered in every snippet when the key is hidden. The real key is
 * only fetched on demand and swapped in when the user reveals it or copies.
 */
const MASKED_KEY = 'tlm_sk_****';

@Component({
  selector: 'app-mcp',
  templateUrl: './mcp.page.html',
  styleUrls: ['./mcp.page.scss']
})
export class McpPage implements OnInit {

  apiKeys: ApiKeyItem[] = [];
  userData: any = null;
  isLoading = true;

  /** MCP server URL shown in every snippet (display only; the app calls `environment.apiDomain`). */
  readonly mcpUrl = 'https://builder-api.totalum.app/api/v1/mcp';
  /** The old URL — a pure alias of the same server, still answering identically. */
  readonly legacyMcpUrl = 'https://api-accounts.totalum.app/api/v1/mcp';

  mcpActiveAgent = 'claude-code';
  mcpCopied = false;

  /** When true, the selected key's real value is rendered in the snippets. */
  mcpShowFullKey = false;
  /** Which API key the snippets/copy use. */
  selectedKeyId: string | null = null;

  constructor(private apiSdkService: ApiSdkService, private router: Router) {}

  async ngOnInit() {
    await this.refreshAll();
  }

  async ionViewWillEnter() {
    if (this.userData) {
      await this.refreshUserDataOnly();
    }
  }

  get apiKeyLocked(): boolean {
    if (!this.userData) return true;
    const purchasedEuros = Number(this.userData.totalCreditsPurchasedEuros ?? 0);
    const balance = Number(this.userData.credits ?? 0);
    return purchasedEuros <= 0 && balance < 1;
  }

  get selectedKey(): ApiKeyItem | null {
    if (!this.apiKeys.length) return null;
    return this.apiKeys.find(k => k._id === this.selectedKeyId) || this.apiKeys[0];
  }

  /** The value rendered inside every snippet — real key only when revealed. */
  get mcpApiKeyDisplay(): string {
    if (this.mcpShowFullKey && this.selectedKey?.decryptedKey) {
      return this.selectedKey.decryptedKey;
    }
    return MASKED_KEY;
  }

  goBuyCredits(): void {
    this.router.navigate(['/account'], { queryParams: { openCredits: 1 } });
  }

  private async refreshAll() {
    this.isLoading = true;
    try {
      const result = await this.apiSdkService.getFullUserData();
      this.userData = result?.success?.data;
      await this.loadApiKeys();
    } catch (error) {
      console.error('Error loading MCP page:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async refreshUserDataOnly() {
    const result = await this.apiSdkService.getFullUserData();
    if (result?.success?.data) {
      this.userData = result.success.data;
    }
  }

  async loadApiKeys() {
    try {
      const result = await this.apiSdkService.getApiKeys();
      this.apiKeys = result?.success?.data || [];
      if (this.apiKeys.length && !this.selectedKeyId) {
        this.selectedKeyId = this.apiKeys[0]._id;
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  }

  async onSelectKey(keyId: string) {
    this.selectedKeyId = keyId;
    if (this.mcpShowFullKey) {
      await this.ensureDecryptedKey(this.selectedKey);
    }
  }

  async toggleShowKey() {
    this.mcpShowFullKey = !this.mcpShowFullKey;
    if (this.mcpShowFullKey) {
      await this.ensureDecryptedKey(this.selectedKey);
    }
  }

  /**
   * Copies the currently active snippet with the REAL api key swapped in for
   * the masked placeholder (works whether or not the key is currently shown).
   */
  async copyMcpSnippet() {
    const el = document.querySelector('.mcp-snippet-active code');
    if (!el) return;
    try {
      const fullKey = await this.getFullKey();
      let text = el.textContent || '';
      if (fullKey) {
        text = text.split(MASKED_KEY).join(fullKey);
      }
      await navigator.clipboard.writeText(text);
      this.mcpCopied = true;
      setTimeout(() => this.mcpCopied = false, 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  }

  private async ensureDecryptedKey(key: ApiKeyItem | null): Promise<void> {
    if (!key || key.decryptedKey) return;
    try {
      const result = await this.apiSdkService.getDecryptedApiKey(key._id);
      key.decryptedKey = result?.success?.data?.apiKey ?? undefined;
    } catch (e) {
      console.error('Error decrypting API key:', e);
    }
  }

  private async getFullKey(): Promise<string | null> {
    const key = this.selectedKey;
    if (!key) return null;
    await this.ensureDecryptedKey(key);
    return key.decryptedKey ?? null;
  }
}
